import { useState } from 'react';
import { Plus, Loader2, Receipt, Lock } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { extractErrorMessage } from '@/lib/error';
import { formatMoney } from '@/lib/money';
import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { SlideOver } from '@/components/ui/SlideOver';
import { useSelectedPropertyId } from '@/contexts/PropertyContext';
import { useAuth } from '@/hooks/useAuth';
import {
  useRetailPaidOuts,
  useCreateRetailPaidOut,
} from '@/hooks/api/useRetailPaidOuts';
import { UserRole } from '@pms/shared';
import type { PaidOutRow } from '@/services/api/retailPaidOuts.api';

const WRITE_ROLES = new Set<UserRole>([
  UserRole.OPERATIONS,
  UserRole.ADMIN,
  UserRole.SUPER_ADMIN,
]);

function fmtDate(iso: string): string {
  try {
    return format(parseISO(iso), 'MMM d, yyyy');
  } catch {
    return iso;
  }
}

// ─── Page ────────────────────────────────────────────────────────────────────

export function PaidOutsPage() {
  const propertyId = useSelectedPropertyId();
  const { user } = useAuth();
  const canRecord = !!user && WRITE_ROLES.has(user.role);
  const [showCreate, setShowCreate] = useState(false);

  const listQuery = useRetailPaidOuts({ propertyId }, !!propertyId);
  const rows: PaidOutRow[] = Array.isArray(listQuery.data)
    ? listQuery.data
    : ((listQuery.data as { items?: PaidOutRow[] } | undefined)?.items ?? []);

  if (user && !canRecord) {
    return (
      <div>
        <PageHeader title="Paid Outs" description="Till-cash disbursements" />
        <EmptyState icon={Lock} title="No access" description="You don't have access to paid-outs." />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Paid Outs"
        description="Till-cash disbursements — an immutable ledger; correct an entry by recording an offsetting one."
        actions={
          <Button onClick={() => setShowCreate(true)} disabled={!propertyId}>
            <Plus size={16} aria-hidden="true" />
            Record Paid Out
          </Button>
        }
      />

      {!propertyId ? (
        <EmptyState icon={Receipt} title="Select a property" description="Select a property to continue." />
      ) : listQuery.isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={32} className="animate-spin text-primary-500" aria-label="Loading" />
        </div>
      ) : listQuery.isError ? (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">
          {extractErrorMessage(listQuery.error)}
        </p>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={Receipt}
          title="No paid-outs"
          description="Record a till-cash disbursement to start the ledger."
          actionLabel="Record Paid Out"
          onAction={() => setShowCreate(true)}
        />
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <Th>Date</Th>
                  <Th>Amount</Th>
                  <Th>Reason</Th>
                  <Th>Recipient</Th>
                  <Th>Recorded by</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{fmtDate(r.entryDate)}</td>
                    <td className="px-4 py-3 text-sm font-bold text-gray-900">{formatMoney(r.amount)}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{r.reason}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{r.recipient}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{r.createdByName ?? '--'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <SlideOver isOpen={showCreate} onClose={() => setShowCreate(false)} title="Record Paid Out">
        {propertyId && (
          <RecordPaidOutForm propertyId={propertyId} onClose={() => setShowCreate(false)} />
        )}
      </SlideOver>
    </div>
  );
}

// ─── Record form ─────────────────────────────────────────────────────────────

function RecordPaidOutForm({
  propertyId,
  onClose,
}: {
  propertyId: string;
  onClose: () => void;
}) {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [recipient, setRecipient] = useState('');
  const [date, setDate] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [note, setNote] = useState('');

  const createMutation = useCreateRetailPaidOut();

  const parsedAmount = parseFloat(amount);
  const isValid =
    Number.isFinite(parsedAmount) &&
    parsedAmount >= 0.01 &&
    reason.trim().length > 0 &&
    recipient.trim().length > 0;

  function clearError() {
    if (createMutation.isError) createMutation.reset();
  }

  function normalizeAmount() {
    if (!amount.trim()) return;
    const n = parseFloat(amount);
    if (Number.isFinite(n) && n >= 0) setAmount(n.toFixed(2));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isValid) return;
    createMutation.mutate(
      {
        propertyId,
        amount: parsedAmount,
        reason: reason.trim(),
        recipient: recipient.trim(),
        date,
        ...(note.trim() ? { note: note.trim() } : {}),
      },
      { onSuccess: () => onClose() },
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
        Paid-outs are immutable once recorded. To correct one, record an offsetting entry.
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor="po-amount" className="block text-sm font-medium text-gray-700 mb-1">
            Amount <span className="text-red-500">*</span>
          </label>
          <input
            id="po-amount"
            type="number"
            step="0.01"
            min="0.01"
            required
            value={amount}
            onChange={(e) => { setAmount(e.target.value); clearError(); }}
            onBlur={normalizeAmount}
            placeholder="0.00"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <div>
          <label htmlFor="po-date" className="block text-sm font-medium text-gray-700 mb-1">Date</label>
          <input
            id="po-date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </div>

      <div>
        <label htmlFor="po-reason" className="block text-sm font-medium text-gray-700 mb-1">
          Reason <span className="text-red-500">*</span>
        </label>
        <input
          id="po-reason"
          type="text"
          required
          value={reason}
          onChange={(e) => { setReason(e.target.value); clearError(); }}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>

      <div>
        <label htmlFor="po-recipient" className="block text-sm font-medium text-gray-700 mb-1">
          Recipient <span className="text-red-500">*</span>
        </label>
        <input
          id="po-recipient"
          type="text"
          required
          value={recipient}
          onChange={(e) => { setRecipient(e.target.value); clearError(); }}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>

      <div>
        <label htmlFor="po-note" className="block text-sm font-medium text-gray-700 mb-1">Note</label>
        <input
          id="po-note"
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Optional"
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>

      {createMutation.isError && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">
          {extractErrorMessage(createMutation.error)}
        </p>
      )}

      <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
        <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
        <Button type="submit" isLoading={createMutation.isPending} disabled={!isValid}>Record</Button>
      </div>
    </form>
  );
}

// ─── Shared ──────────────────────────────────────────────────────────────────

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50">
      {children}
    </th>
  );
}
